import {
  COLLECTION_COVER_SLUGS,
  COLLECTION_COVER_DARK_SLUGS,
} from './collection-covers.generated'

/**
 * Derives a cover slug from a collection's display name.
 *
 * Convention (must match the background job that generates the files AND the
 * `collection_cover_slug()` SQL function in
 * supabase/migrations/20260809_collection_cover_slug.sql):
 *   slug = name.toLowerCase()
 *              .replace(/&/g, ' ')
 *              .replace(/[^a-z0-9]+/g, '-')
 *              .replace(/^-+|-+$/g, '')
 *
 * Examples:
 *   "Battle Royale Pack"   -> battle-royale-pack
 *   "90s Nostalgia"        -> 90s-nostalgia
 *   "Nature's Soundtrack"  -> nature-s-soundtrack   (apostrophe footgun)
 *   "Pokémon Universe"     -> pok-mon-universe      (accent footgun)
 *
 * This is only the FALLBACK for rows predating the `cover_slug` column
 * (TES-1604) — prefer the stored slug via `collectionCoverSlug()`.
 */
export function slugifyCollection(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * The minimum shape needed to resolve a cover image. Accepts a bare name for
 * curated/in-memory collections that have no DB row.
 */
export type CollectionCoverRef = string | { name: string; cover_slug?: string | null }

/**
 * The slug a collection's cover image is addressed by.
 *
 * Prefers the stable `cover_slug` column, which is pinned at insert time and
 * deliberately NOT re-derived on rename. Deriving from the mutable display
 * name is what silently delisted "Wild Kingdom" for ~2 months (TES-1604);
 * that path now only serves callers with no DB row to read from.
 */
export function collectionCoverSlug(ref: CollectionCoverRef): string {
  if (typeof ref === 'string') return slugifyCollection(ref)
  return ref.cover_slug?.trim() || slugifyCollection(ref.name)
}

export function collectionCoverImage(ref: CollectionCoverRef): string {
  return `/images/collections/${collectionCoverSlug(ref)}.webp`
}

/**
 * The dark-theme companion of a collection's cover, or undefined when it has
 * none.
 *
 * Only the line-drawn covers need one: they are ink on a transparent ground,
 * so a single file cannot serve both canvases — black strokes vanish on the
 * dark canvas. The older full-colour covers bake in their own ground and
 * render unchanged in either theme, so a caller must treat `undefined` as
 * "show the base cover in both themes", never as "no cover".
 *
 * The file is `<slug>.dark.webp`. The DOT is deliberate: slugify collapses
 * every non-alphanumeric run to "-", so no real slug can contain a dot, while
 * plenty legitimately end in "-dark" (the "After Dark" collection ships a
 * genuine after-dark.webp).
 */
export function collectionCoverImageDark(ref: CollectionCoverRef): string | undefined {
  const slug = collectionCoverSlug(ref)
  return COLLECTION_COVER_DARK_SLUGS.has(slug)
    ? `/images/collections/${slug}.dark.webp`
    : undefined
}

/**
 * The dark companion for an already-resolved cover PATH, for callers holding
 * a literal path rather than a collection row (see `hasCollectionCoverPath`).
 */
export function collectionCoverPathDark(path: string | undefined | null): string | undefined {
  if (!path) return undefined
  const match = /\/images\/collections\/([^/]+)\.webp$/.exec(path)
  if (!match || !COLLECTION_COVER_DARK_SLUGS.has(match[1])) return undefined
  return `/images/collections/${match[1]}.dark.webp`
}

/**
 * True when the collection has a real cover image on disk
 * (public/images/collections/<slug>.webp). Collections without a cover are
 * hidden from every frontend listing (filtered in collectionsApi.getCollections)
 * so no coverless / broken-image collection renders — but a *public* coverless
 * collection is now reported to Sentry rather than dropped silently. Regenerate
 * the slug set after adding covers: `node scripts/gen-collection-covers.mjs`.
 */
export function hasCollectionCover(ref: CollectionCoverRef): boolean {
  return COLLECTION_COVER_SLUGS.has(collectionCoverSlug(ref))
}

/**
 * True when an already-resolved cover PATH points at a file that exists on
 * disk. For callers holding a literal path rather than a collection row —
 * `curatedCollections[].coverImage` is hardcoded in source and can therefore
 * drift from `public/images/collections/` without anything noticing.
 *
 * Deliberately NOT a way around `hasCollectionCover(row)`: DB-backed
 * collections must still be checked by row so the cover keys on the stable
 * `cover_slug` column (TES-1604), never on a mutable display name.
 */
export function hasCollectionCoverPath(path: string | undefined | null): boolean {
  if (!path) return false
  const match = /\/images\/collections\/([^/]+)\.webp$/.exec(path)
  return match ? COLLECTION_COVER_SLUGS.has(match[1]) : false
}
